/**
 * Guest Workspace tab bodies — pure display, no local state.
 */
import { StatusChip, OperationalCard, RoomOperationalCard } from "@/components/operational";
import { Phone, Mail, IdCard, Calendar, MapPin, User, StickyNote, Receipt } from "lucide-react";

// ── Shared helpers ──────────────────────────────────────────────
function KV({ label, value, testid }) {
  return (
    <div className="grid grid-cols-[140px_1fr] items-start gap-3 py-1.5" data-testid={testid}>
      <span className="text-[12px] uppercase tracking-wider text-slate-500">{label}</span>
      <span className="text-[13px] text-slate-800">{value || "—"}</span>
    </div>
  );
}

function EmptyState({ label, testid }) {
  return (
    <div className="py-16 text-center text-slate-400 text-[14px]" data-testid={testid}>
      {label}
    </div>
  );
}

// ── Overview ────────────────────────────────────────────────────
export function OverviewTab({ workspace, money }) {
  const g = workspace?.guest;
  const stay = workspace?.active_stay;
  const folio = workspace?.active_folio;
  const room = workspace?.active_room;
  if (!g) return null;
  return (
    <div className="p-5 space-y-5" data-testid="tab-overview">
      <OperationalCard>
        <div className="mb-3 flex items-center gap-2">
          <User className="w-4 h-4 text-slate-500"/>
          <span className="text-[13px] uppercase tracking-wider text-slate-500 font-semibold">Guest</span>
        </div>
        <KV label="Name" value={g.display_name} testid="ov-name"/>
        <KV label="VIP" value={g.is_vip_effective ? "Yes" : "No"} testid="ov-vip"/>
        <KV label="Phone" value={g.primary_phone || g.phone}/>
        <KV label="Email" value={g.primary_email || g.email}/>
        <KV label="Nationality" value={g.nationality}/>
        <KV label="Govt ID" value={g.govt_id_number ? `${g.govt_id_type || ""} ${g.govt_id_number}` : "—"}/>
      </OperationalCard>

      {stay && (
        <OperationalCard>
          <div className="mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-500"/>
            <span className="text-[13px] uppercase tracking-wider text-slate-500 font-semibold">Current Stay</span>
            <StatusChip status={stay.status === "checked_in" ? "active" : "pending"} label={stay.status} size="sm"/>
          </div>
          <KV label="Stay code" value={stay.code}/>
          <KV label="Arrival" value={stay.expected_arrival}/>
          <KV label="Departure" value={stay.expected_departure}/>
          <KV label="Adults / Children" value={`${stay.num_adults || 0} / ${stay.num_children || 0}`}/>
          {folio && <KV label="Folio balance" value={money(folio.balance_minor_cache || 0)}/>}
        </OperationalCard>
      )}

      {room && (
        <OperationalCard>
          <div className="mb-3 flex items-center gap-2">
            <span className="text-[13px] uppercase tracking-wider text-slate-500 font-semibold">Current Room</span>
          </div>
          <RoomOperationalCard
            room={{
              id: room.id,
              number: room.room_number,
              floor: room.floor_number || room.floor,
              type_label: room.room_type_name || room.type,
              snapshot_status: room?.snapshot?.current_status,
              current_guest: g.display_name,
              vip: g.is_vip_effective,
            }}
            size="md"
            testid="ov-room"
          />
        </OperationalCard>
      )}
    </div>
  );
}

// ── Reservation ─────────────────────────────────────────────────
export function ReservationTab({ workspace }) {
  const items = workspace?.reservations || [];
  if (items.length === 0) return <EmptyState label="No reservations" testid="tab-reservation-empty"/>;
  return (
    <div className="p-5 space-y-3" data-testid="tab-reservation">
      {items.map(r => (
        <OperationalCard key={r.id} testid={`res-${r.id}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-[14px]">{r.code || r.reservation_code}</div>
            <StatusChip status={r.status === "confirmed" ? "ready" : "pending"} label={r.status} size="sm"/>
          </div>
          <KV label="Arrival" value={r.arrival_date}/>
          <KV label="Departure" value={r.expected_departure}/>
          <KV label="Source" value={r.booking_source}/>
          <KV label="Adults / Children" value={`${r.num_adults || 0} / ${r.num_children || 0}`}/>
        </OperationalCard>
      ))}
    </div>
  );
}

// ── Stay ────────────────────────────────────────────────────────
export function StayTab({ workspace }) {
  const items = workspace?.stays || [];
  if (items.length === 0) return <EmptyState label="No stays" testid="tab-stay-empty"/>;
  return (
    <div className="p-5 space-y-3" data-testid="tab-stay">
      {items.map(s => (
        <OperationalCard key={s.id} testid={`stay-${s.id}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-[14px]">{s.code}</div>
            <StatusChip status={s.status === "checked_in" ? "active" : s.status === "checked_out" ? "ready" : "pending"}
                         label={s.status} size="sm"/>
          </div>
          <KV label="Arrival" value={s.expected_arrival}/>
          <KV label="Departure" value={s.expected_departure}/>
          <KV label="Room" value={s.current_room_id}/>
          <KV label="Actual arrival" value={s.actual_arrival || "—"}/>
          <KV label="Actual departure" value={s.actual_departure || "—"}/>
        </OperationalCard>
      ))}
    </div>
  );
}

// ── Room ────────────────────────────────────────────────────────
export function RoomTab({ workspace }) {
  const room = workspace?.active_room;
  const stays = workspace?.stays || [];
  const history = stays.flatMap(s => (s.room_history || []).map(h => ({ ...h, stay_code: s.code })));
  return (
    <div className="p-5 space-y-4" data-testid="tab-room">
      {room ? (
        <RoomOperationalCard
          room={{
            id: room.id,
            number: room.room_number,
            floor: room.floor_number || room.floor,
            type_label: room.room_type_name || room.type,
            snapshot_status: room?.snapshot?.current_status,
          }}
          size="lg"
        />
      ) : (
        <EmptyState label="No active room assigned"/>
      )}

      <OperationalCard>
        <div className="mb-3 text-[13px] uppercase tracking-wider text-slate-500 font-semibold">Room History</div>
        {history.length === 0 ? (
          <div className="text-[13px] text-slate-400">No history</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {history.map((h, i) => (
              <li key={i} className="py-2 text-[13px] flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">Room {h.room_number || h.room_id}</div>
                  <div className="text-[11px] text-slate-500">{h.stay_code} · {h.reason || "—"}</div>
                </div>
                <div className="text-[11px] text-slate-500 text-right">
                  <div>{(h.assigned_at || "").slice(0, 19).replace("T", " ")}</div>
                  {h.released_at && <div>→ {h.released_at.slice(0, 19).replace("T", " ")}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </OperationalCard>
    </div>
  );
}

// ── Folio ───────────────────────────────────────────────────────
export function FolioTab({ workspace, money, onOpenAction }) {
  const stays = workspace?.stays || [];
  const active = workspace?.active_folio;
  return (
    <div className="p-5 space-y-4" data-testid="tab-folio">
      {active && (
        <OperationalCard tone="info">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Active Folio</div>
              <div className="font-semibold">{active.folio_no || active.id}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Balance</div>
              <div className="text-[24px] font-bold text-sky-800">{money(active.balance_minor_cache || 0)}</div>
            </div>
          </div>
        </OperationalCard>
      )}
      <div className="flex flex-wrap gap-2">
        <button className="px-4 py-2 rounded-lg border-2 border-slate-300 hover:border-sky-500 min-h-[48px] text-[14px] font-medium"
                onClick={() => onOpenAction?.("quick-charge")}
                data-testid="folio-post-charge">
          Post Charge
        </button>
        <button className="px-4 py-2 rounded-lg border-2 border-emerald-500 bg-emerald-50 text-emerald-800 hover:border-emerald-600 min-h-[48px] text-[14px] font-medium"
                onClick={() => onOpenAction?.("quick-payment")}
                data-testid="folio-collect-payment">
          Collect Payment
        </button>
      </div>
      <OperationalCard>
        <div className="mb-3 text-[13px] uppercase tracking-wider text-slate-500 font-semibold">Folios</div>
        {stays.map(s => s.folio ? (
          <div key={s.folio.id} className="flex items-center justify-between py-2 border-b border-slate-100 text-[13px]">
            <div>
              <div className="font-medium">{s.folio.folio_no}</div>
              <div className="text-[11px] text-slate-500">Stay {s.code} · {s.folio.status}</div>
            </div>
            <div className="text-right font-semibold">{money(s.folio.balance_minor_cache || 0)}</div>
          </div>
        ) : null)}
      </OperationalCard>
    </div>
  );
}

// ── Payments ────────────────────────────────────────────────────
export function PaymentsTab({ workspace, money }) {
  const items = workspace?.payments || [];
  if (items.length === 0) return <EmptyState label="No payments" testid="tab-payments-empty"/>;
  return (
    <div className="p-5 space-y-3" data-testid="tab-payments">
      {items.map(p => (
        <OperationalCard key={p.id} testid={`pay-${p.id}`} padded={false}>
          <div className="p-4 flex items-center justify-between">
            <div>
              <div className="font-medium text-[14px]">{p.payment_no}</div>
              <div className="text-[11px] text-slate-500">
                {(p.received_at || "").slice(0, 19).replace("T", " ")} · {p.method} · {p.purpose}
              </div>
            </div>
            <div className="text-right">
              <div className="font-semibold">{money(p.amount_minor || 0)}</div>
              <div className="text-[11px]"><StatusChip status={p.status === "completed" ? "ready" : "pending"} label={p.status} size="sm"/></div>
            </div>
          </div>
        </OperationalCard>
      ))}
    </div>
  );
}

// ── Timeline ────────────────────────────────────────────────────
export function TimelineTab({ timeline }) {
  if (!timeline || timeline.length === 0) return <EmptyState label="No timeline events" testid="tab-timeline-empty"/>;
  return (
    <div className="p-5 space-y-2" data-testid="tab-timeline">
      {timeline.map((e, i) => (
        <div key={i} className="flex gap-3 py-2 border-b border-slate-100 text-[13px]" data-testid={`tl-${i}`}>
          <div className="w-24 shrink-0">
            <StatusChip status="ready" label={e.kind} size="sm"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium">{e.event}</div>
            {e.note && <div className="text-[12px] text-slate-600">{e.note}</div>}
            <div className="text-[11px] text-slate-400">{(e.at || "").slice(0, 19).replace("T", " ")}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Documents ───────────────────────────────────────────────────
export function DocumentsTab({ workspace }) {
  const items = workspace?.documents || [];
  if (items.length === 0) return <EmptyState label="No documents" testid="tab-documents-empty"/>;
  return (
    <div className="p-5 space-y-3" data-testid="tab-documents">
      {items.map(d => (
        <OperationalCard key={d.id} testid={`doc-${d.id}`}>
          <div className="font-medium">{d.filename || d.name}</div>
          <div className="text-[11px] text-slate-500">{d.doc_type || d.type} · {(d.uploaded_at || d.created_at || "").slice(0, 10)}</div>
        </OperationalCard>
      ))}
    </div>
  );
}

// ── Notes ───────────────────────────────────────────────────────
export function NotesTab({ workspace, onOpenAction }) {
  const items = workspace?.notes || [];
  return (
    <div className="p-5 space-y-3" data-testid="tab-notes">
      <button className="min-h-[56px] w-full rounded-xl border-2 border-dashed border-slate-300 hover:border-sky-500 text-[14px] font-medium text-slate-600 flex items-center justify-center gap-2"
              onClick={() => onOpenAction?.("note")}
              data-testid="notes-add">
        <StickyNote className="w-4 h-4"/> Add Note
      </button>
      {items.length === 0 ? (
        <EmptyState label="No notes yet" testid="tab-notes-empty"/>
      ) : items.map(n => (
        <OperationalCard key={n.id} testid={`note-${n.id}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="font-semibold text-[14px]">{n.subject}</div>
            <StatusChip status={n.state === "active" ? "pending" : "ready"} label={n.state} size="sm"/>
          </div>
          <div className="text-[12px] text-slate-500 mb-1">{n.kind}</div>
          {n.body && <div className="text-[13px] text-slate-700">{n.body}</div>}
          <div className="text-[11px] text-slate-400 mt-1">{(n.created_at || "").slice(0, 19).replace("T", " ")}</div>
        </OperationalCard>
      ))}
    </div>
  );
}

// Default export map
export default { OverviewTab, ReservationTab, StayTab, RoomTab, FolioTab, PaymentsTab, TimelineTab, DocumentsTab, NotesTab };
